import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { convertLedgerPdf, fetchLedgerPdfFormats, LedgerPdfFormat, LedgerPdfSummary } from '../../api/batch12Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Choose the software that generated the nominal ledger PDF (Taxcalc, Iris, Capium or Digita).',
  'Upload the PDF.',
  'Click Convert. A ZIP downloads with the full ledger (one row per transaction) and the AP/BK mapping workbook.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const FALLBACK: LedgerPdfFormat[] = ['taxcalc', 'iris', 'capium', 'digita'].map((key) => ({
  key,
  label: key.charAt(0).toUpperCase() + key.slice(1),
  mapping_available: true,
}));

function LedgerPdfPanel({ mission, onBack }: PanelProps) {
  const [formats, setFormats] = useState<LedgerPdfFormat[]>(FALLBACK);
  const [format, setFormat] = useState('taxcalc');
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<LedgerPdfSummary>>();

  useEffect(() => {
    fetchLedgerPdfFormats().then(setFormats).catch(() => undefined);
  }, []);

  const current = formats.find((f) => f.key === format);

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the nominal ledger PDF first.');
      return;
    }
    await run(() => convertLedgerPdf(files[0], format), 'Conversion failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="lp-format" className="mb-1.5">
          PDF format
        </Label>
        <select
          id="lp-format"
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          {formats.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">Choose the software that generated the nominal ledger PDF.</p>
        {current && !current.mapping_available && (
          <p className="mt-2 text-xs text-amber-700">
            The AP/BK mapping config for {current.label} is not installed on the backend yet: you will get the full ledger only.
          </p>
        )}
      </div>

      <FileDrop
        label="Nominal ledger PDF"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Converting…' : 'Convert'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Converting…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ Extracted {s.transactions} transactions across {s.ap_codes} AP codes ({s.format}). {result.filename} downloaded.
          </div>
          <ul className="list-disc pl-5 text-xs text-muted-foreground">
            {s.files.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
          {s.mapping_rows > 0 && (
            <p className="text-xs text-muted-foreground">
              AP/BK mapping: {s.mapping_rows} rows · {s.mapping_ap_codes} AP codes.
            </p>
          )}
          {s.mapping_note && <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">{s.mapping_note}</div>}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload a PDF above and click Convert to begin.</InfoNotice>}
    </MissionShell>
  );
}

export default LedgerPdfPanel;
